import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAtom } from 'jotai'
import { userAtom } from '../../state/auth'
import { workflowStageDefinitionsAtom, workflowStagesLoadingAtom } from '../../state/clients'
import { portalApi, workflowStagesApi, PortalDashboardData } from '../../services/api'
import { buildPortalVisibility } from '../../constants/stageVisibility'
import { Box, Skeleton } from '@mui/material'
import { colors } from '../../theme'

// Matches PortalLayout's drawerWidth. Kept here rather than exported from the
// layout because the provider renders before the layout mounts.
const portalRailWidth = 260

interface PortalData {
  clientId: string
  leadId?: string
  workflowStage: string
  dashboard: PortalDashboardData | null
  visibility: ReturnType<typeof buildPortalVisibility>
  refresh: () => Promise<void>
}

const PortalDataContext = createContext<PortalData | null>(null)

export function usePortalData(): PortalData {
  const data = useContext(PortalDataContext)
  if (!data) {
    throw new Error('usePortalData must be used inside PortalDataProvider')
  }
  return data
}

/**
 * The one portal-dashboard read for the whole portal session, and the stage
 * visibility derived from it. Sits between ProtectedPortalRoute and
 * PortalLayout, where the route element is stable, so navigating between portal
 * pages does not refetch.
 *
 * Every failure direction shows more, never less: a dashboard fetch that does
 * not land leaves the content rule switched off rather than guessing a page is
 * empty, and a stage with no configuration hides nothing.
 */
export default function PortalDataProvider({ children }: { children: React.ReactNode }) {
  const [user] = useAtom(userAtom)
  const [workflowStages, setWorkflowStages] = useAtom(workflowStageDefinitionsAtom)
  const [workflowStagesLoading, setWorkflowStagesLoading] = useAtom(workflowStagesLoadingAtom)
  const [dashboard, setDashboard] = useState<PortalDashboardData | null>(null)
  const [dashboardSettled, setDashboardSettled] = useState(false)
  const [ready, setReady] = useState(false)

  // ProtectedPortalRoute blocks until this is non-empty, so every portal child
  // can treat it as a fact. Taking it from the JWT rather than the dashboard is
  // what keeps a failed fetch costing the gates and nothing else.
  const clientId = user?.clientId || ''

  const loadDashboard = useCallback(async () => {
    try {
      setDashboard(await portalApi.getDashboard())
    } catch (err) {
      console.error('Failed to load portal dashboard:', err)
      setDashboard(null)
    } finally {
      setDashboardSettled(true)
    }
  }, [])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  // Rendering before both have settled would mount every gated page, fire its
  // requests and unmount it again — a visible flash of content an admin chose
  // to hide. The gate latches once opened so a later refresh() cannot pull the
  // whole portal back down to a spinner.
  useEffect(() => {
    if (dashboardSettled && !workflowStagesLoading) setReady(true)
  }, [dashboardSettled, workflowStagesLoading])

  const refresh = useCallback(async () => {
    await Promise.all([
      loadDashboard(),
      (async () => {
        try {
          setWorkflowStagesLoading(true)
          // Forced: the stage catalogue is cached five minutes per lambda
          // container, and the reason to refresh is that it just changed.
          setWorkflowStages(await workflowStagesApi.list(true))
        } catch (err) {
          console.error('Failed to reload workflow stages:', err)
        } finally {
          setWorkflowStagesLoading(false)
        }
      })(),
    ])
  }, [loadDashboard, setWorkflowStages, setWorkflowStagesLoading])

  const workflowStage = dashboard?.client.workflowStage || ''

  const hiddenPanels = useMemo(
    () => workflowStages.find((s) => s.stageId === workflowStage)?.hiddenPanels,
    [workflowStages, workflowStage]
  )

  const value = useMemo<PortalData>(
    () => ({
      clientId,
      leadId: dashboard?.client.leadId,
      workflowStage,
      dashboard,
      visibility: buildPortalVisibility(hiddenPanels, dashboard?.contentAvailability ?? null),
      refresh,
    }),
    [clientId, dashboard, workflowStage, hiddenPanels, refresh]
  )

  // The portal's shell, not a centred spinner. This provider wraps PortalLayout,
  // so a spinner here meant a client watched an empty white page and then the
  // whole portal — rail, header and content — appear at once. The shell is
  // known before any request answers, so it is drawn first and only the content
  // waits.
  if (!ready) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Box
          sx={{
            width: portalRailWidth,
            flexShrink: 0,
            backgroundColor: colors.gray900,
            display: { xs: 'none', sm: 'block' },
            p: 3,
          }}
        >
          <Skeleton variant="rounded" width={132} height={34} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Skeleton variant="text" width={80} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.1)', mt: 1 }} />
          <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={26}
                sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}
              />
            ))}
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ height: 64, backgroundColor: colors.gray900 }} />
          <Box sx={{ p: 3 }}>
            <Skeleton variant="text" width={200} height={26} />
            <Skeleton variant="text" width={300} height={20} sx={{ mb: 3 }} />
            <Skeleton variant="rounded" height={180} sx={{ borderRadius: '12px', mb: 2 }} />
            <Skeleton variant="rounded" height={240} sx={{ borderRadius: '12px' }} />
          </Box>
        </Box>
      </Box>
    )
  }

  return <PortalDataContext.Provider value={value}>{children}</PortalDataContext.Provider>
}
